import React from 'react'
import { ApplyColumnStateParams, GridApi } from 'ag-grid-community'
import { noop } from 'lodash-es'
import { getTextFlagButtonState, TextFlagButtonsContainer, ToolbarTextFlagButton } from '../buttons'
import { Locale, Translation } from 'locale'

interface Group {
  /** The given ag grid column state to be ordered, sort etc when the grouping is selected. */
  columnState: ApplyColumnStateParams
  /** The ColumnIds that are set in the grouping, it can be empty for ungrouped,
   * or it can have one or multimple items.
   * When receiving multiple items the order is critical.
   * As an example [DealsGridColumnId.COUNTERPARTY_NAME, CollectionColumnId.COLLECTION_NAME]
   * it means that first is grouped by counterparty, and then inside each counterparty is grouped
   * by collection name. */
  rowGroupColumnIds: string[]
  /** Name property it's needed if the grouping has to be rendered, it will be the string to show on the button. */
  name?: string
}

export type GroupsMap = Record<string, Group>

export interface GroupByTogglesProps {
  gridApi: GridApi | null
  groupsMap: GroupsMap
}

type CUSTOM = 'CUSTOM'
const CUSTOM = 'CUSTOM' as const

interface CurrentGrouping {
  key: string | CUSTOM
  rowGroupColumnIds: string[]
}

const getCurrentGrouping = (gridApi: GridApi | null, groupsMap: GroupsMap): CurrentGrouping => {
  const rowGroupColumnIds = gridApi?.getRowGroupColumns()?.map(column => column.getColId())

  const found = Object.entries(groupsMap).find(([_key, groupMap]) => {
    return (
      groupMap.rowGroupColumnIds.length === rowGroupColumnIds?.length &&
      groupMap.rowGroupColumnIds.every((colId, index) => colId === rowGroupColumnIds?.[index])
    )
  })

  if (found) {
    const [key, group] = found

    return {
      key,
      rowGroupColumnIds: group.rowGroupColumnIds
    }
  }

  return { key: CUSTOM, rowGroupColumnIds: rowGroupColumnIds ?? [] }
}

const DEFAULT_UNGROUP: Group = {
  rowGroupColumnIds: [],
  columnState: { defaultState: { sort: null } }
}

export const GroupByToggles = ({ gridApi, groupsMap }: GroupByTogglesProps) => {
  const currentGrouping = getCurrentGrouping(gridApi, groupsMap)

  const update = (option: string) => {
    if (!gridApi) {
      return
    }

    const [ungroupKey = ''] =
      Object.entries(groupsMap).find(([_key, group]) => {
        return group.rowGroupColumnIds.length === 0
      }) ?? []

    const isUngrouping = option === ungroupKey || option === currentGrouping.key

    if (isUngrouping) {
      const groupMap = groupsMap[ungroupKey] ?? DEFAULT_UNGROUP
      gridApi.applyColumnState(groupMap.columnState)
      gridApi.setRowGroupColumns(groupMap.rowGroupColumnIds)
    } else if (groupsMap?.[option]) {
      const groupMap = groupsMap[option]
      gridApi.applyColumnState(groupMap.columnState)
      gridApi.setRowGroupColumns(groupMap.rowGroupColumnIds)
    } else {
      throw new Error(`Unhandled Group by Option ${[option]}`)
    }
  }

  const handleSelection = (key: string) => {
    const currentRowGroupColumnIds = currentGrouping?.rowGroupColumnIds ?? []
    const [selectedOption] = groupsMap[key].rowGroupColumnIds

    const unsortedNewColumnIds = currentRowGroupColumnIds.includes(selectedOption)
      ? currentRowGroupColumnIds.filter(columnId => columnId !== selectedOption)
      : [selectedOption, ...currentRowGroupColumnIds]

    const [groupKeyFound] =
      Object.entries(groupsMap).find(([_key, groupMap]) => {
        return (
          unsortedNewColumnIds.length === groupMap.rowGroupColumnIds.length &&
          groupMap.rowGroupColumnIds.every(columnId => unsortedNewColumnIds.includes(columnId))
        )
      }) ?? []

    return groupKeyFound ?? key
  }

  const handleUpdate = (key: string) => {
    const selection = handleSelection(key)
    update(selection)
  }

  return (
    <TextFlagButtonsContainer>
      {Object.entries(groupsMap)
        .filter(([_key, group]) => group.rowGroupColumnIds.length <= 1 && group?.name)
        .map(([key, group]) => {
          const [columnId] = group.rowGroupColumnIds
          return (
            <ToolbarTextFlagButton
              key={key}
              dataTestId={`GroupByToggles-ToolbarTextFlagButton-Grouping${group.name}`}
              state={getTextFlagButtonState(currentGrouping.key === key || !!currentGrouping.rowGroupColumnIds?.includes?.(columnId))}
              onClick={() => handleUpdate(key)}>
              {group.name}
            </ToolbarTextFlagButton>
          )
        })}
      {currentGrouping.key === CUSTOM && (
        <ToolbarTextFlagButton
          dataTestId={`GroupByToggles-ToolbarTextFlagButton-${CUSTOM}`}
          state={getTextFlagButtonState(currentGrouping.key === CUSTOM)}
          disabled={true}
          onClick={noop}>
          {Locale.translate(Translation.TC_GROUPING_CUSTOM)}
        </ToolbarTextFlagButton>
      )}
    </TextFlagButtonsContainer>
  )
}
